// AI views: AIImportView, ValidationView, ExtractionDashView,
// RedlineReview, DraftingView.
//
// AIImportView is the bulk PDF→contract import flow with batch
// processing, AI field extraction, parent matching, and dedup checks.
// ValidationView is the human review queue for AI-extracted fields.
// ExtractionDashView shows extraction stats and quality metrics.
// RedlineReview displays AI-suggested redlines from playbook analysis.
// DraftingView is the contract drafting assistant with chat + tools.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { diffWords } from "diff";
import { T } from "../theme";
import { fmtDate, toTitleCase } from "../utils";
import { apiCall, apiCallEx, errorText } from "../apiClient";
import { AI_UNIVERSAL_FIELDS } from "../constants";
import { renderMarkdown } from "./markdown";
import { PdfHighlightModal } from "./PdfHighlightModal";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";

// Validate an extraction row before allowing import. Blocks approval when:
// - A field has a value with low confidence (AI extracted something but is uncertain)
// - A field value contains placeholder markers ([brackets], "insert here",
//   "TBD", "to be determined", etc.) — the AI hallucinated a placeholder
// - A field is empty but the AI is confident it SHOULD be present (rare)
//
// Does NOT block on: empty field + low confidence. That's the AI saying the
// field is not applicable / not in this document — a normal case (e.g., an MSA
// doesn't have "Amendment Effective Date" or "Sub-processor Requirements").
// Returns { ok, lowConf: string[], missing: string[], placeholder: string[] }.
const PLACEHOLDER_RE = /[\[\]<>{}]|insert\s|tbd|to\s+be\s+determined|placeholder|\bn\/?a\b/i;
const validateImportRow = (row: any): { ok: boolean; lowConf: string[]; missing: string[]; placeholder: string[]; blocking: string[] } => {
  const lowConf: string[] = [];
  const missing: string[] = [];
  const placeholder: string[] = [];
  Object.entries(row).forEach(([k, v]: [string, any]) => {
    if (k.startsWith('__') || k === 'suggestedRelationship') return;
    if (!v || typeof v !== 'object') return;
    const val = (v.v == null ? '' : String(v.v)).trim();
    const conf = (v.conf || v.confidence || 'medium').toLowerCase();
    if (!val) { if (conf !== 'low') missing.push(k); return; }
    if (PLACEHOLDER_RE.test(val)) { placeholder.push(k); return; }
    if (conf === 'low') { lowConf.push(k); }
  });
  const blocking = [...missing, ...placeholder, ...lowConf];
  return { ok: blocking.length === 0, lowConf, missing, placeholder, blocking };
};


/* ═══════════════════ VALIDATION QUEUE ═══════════════════ */
export const ValidationView = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadValidation = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const data = await apiCall("/extraction/validation");
    if (data?.items) setItems(data.items.map((it: any) => ({ id: it.id, file: it.file_name || it.contract_name || "Unknown", field: it.field_name, value: it.field_value, conf: it.confidence })));
    setLoading(false);
  }, []);

  useEffect(() => { loadValidation(true); }, [loadValidation]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => loadValidation(false), 30000);
    return () => clearInterval(interval);
  }, [loadValidation]);

  const [validateError, setValidateError] = useState<string | null>(null);

  const handleValidate = async (id: string, action: string, newValue: any = undefined) => {
    // The result used to be ignored entirely and the row removed from the queue
    // regardless. A failed validation therefore DISAPPEARED: the reviewer saw it
    // leave the list and believed the item was handled when nothing was written.
    // The row now stays put and says why.
    const r = await apiCallEx(`/extraction/validate/${id}`, { method: "PUT", body: JSON.stringify({ action, new_value: newValue }) }, "return");
    if (!r.ok) {
      setValidateError(errorText(r, "That item was not validated — it is still in the queue."));
      return;
    }
    setValidateError(null);
    setItems(prev => prev.filter((it: any) => it.id !== id));
  };

  return (
    <div>
      <SectionTitle sub="AI extractions flagged as low confidence" right={<RefreshBtn onClick={() => loadValidation(false)} />}>Validation Queue</SectionTitle>
      {validateError && (
        <Card style={{ marginBottom: 6, borderColor: "#a33", color: "#fcc", fontSize: 11 }}>
          {validateError}
          <button onClick={() => setValidateError(null)} style={{ marginLeft: 8, background: "none", border: "none", color: "#fcc", cursor: "pointer" }}>x</button>
        </Card>
      )}
      {loading ? <div style={{ textAlign: "center", padding: 40, color: T.txM, fontSize: 12 }}>Loading...</div> :
        items.length === 0 ? <EmptyState icon="◈" msg="No items pending validation" /> : items.map((it: any) => (
        <Card key={it.id} style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}><ConfBadge level={it.conf || "low"} /><div><div style={{ fontSize: 12, color: T.tx, fontWeight: 500 }}>{it.field}</div><div style={{ fontSize: 11, color: T.txM }}>{it.file}</div></div></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input defaultValue={it.value} id={`val-${it.id}`} style={{ padding: "6px 8px", borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", width: 200 }} />
            <Btn v="success" sz="sm" onClick={() => { const el = document.getElementById(`val-${it.id}`); handleValidate(it.id, (el as HTMLInputElement | null)?.value !== it.value ? "edit" : "approve", (el as HTMLInputElement | null)?.value); }}>Approve</Btn>
            <Btn v="danger" sz="sm" onClick={() => handleValidate(it.id, "reject")}>Reject</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
};
